/**
 * Local-First Service
 *
 * Manages local-first configuration and health monitoring for AI components.
 * Works with the backend local-first architecture to prioritize local
 * components over cloud services when available.
 */

import { api } from './api';
import {
  startComponent as startComponentAPI,
  stopComponent as stopComponentAPI,
} from './componentService';
import { RETRY_CONFIGS } from '../constants/timings';
import { getWebSocketUrl } from './urlResolver';

// ============================================================================
// Types
// ============================================================================

export type PreferenceMode = 'local_only' | 'local_first' | 'cloud_first' | 'cloud_only';

export interface ServicePreference {
  mode: PreferenceMode;
  fallbackEnabled: boolean;
  timeoutSeconds: number;
}

export interface LocalFirstConfig {
  llm: ServicePreference;
  tts: ServicePreference;
  translation: ServicePreference;
  transcription: ServicePreference;
  imageGeneration: ServicePreference;
  autoStartComponents: boolean;
}

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

export interface ComponentHealth {
  componentId: string;
  status: HealthStatus;
  isInstalled: boolean;
  isRunning: boolean;
  lastCheck: string | null;
  lastError: string | null;
  uptimeSeconds: number;
  restartCount: number;
  responseTimeMs: number | null;
}

// ============================================================================
// API Functions - Configuration
// ============================================================================

// Backend returns snake_case for config fields
interface BackendServicePreference {
  mode: PreferenceMode;
  fallback_enabled: boolean;
  timeout_seconds: number;
}

interface BackendLocalFirstConfig {
  llm: BackendServicePreference;
  tts: BackendServicePreference;
  translation: BackendServicePreference;
  transcription: BackendServicePreference;
  image_generation: BackendServicePreference;
  auto_start_components: boolean;
  health_check_interval: number;
}

function transformServicePreference(pref: BackendServicePreference): ServicePreference {
  return {
    mode: pref.mode,
    fallbackEnabled: pref.fallback_enabled,
    timeoutSeconds: pref.timeout_seconds,
  };
}

function transformConfig(data: BackendLocalFirstConfig): LocalFirstConfig {
  return {
    llm: transformServicePreference(data.llm),
    tts: transformServicePreference(data.tts),
    translation: transformServicePreference(data.translation),
    transcription: transformServicePreference(data.transcription),
    imageGeneration: transformServicePreference(data.image_generation),
    autoStartComponents: data.auto_start_components,
  };
}

// Backend wraps responses in { success: true, data: ... }
interface BackendEnvelope<T> {
  success: boolean;
  data: T;
}

/**
 * Get the current local-first configuration
 */
export async function getLocalFirstConfig(): Promise<LocalFirstConfig> {
  // Backend ALWAYS wraps in { success, data } (api_server/local_routes/
  // local_first.py). No dual-envelope fallback (RULE #-2): one format.
  const response = await api.get<BackendEnvelope<BackendLocalFirstConfig>>(
    '/api/v1/local-first/config'
  );
  return transformConfig(response.data.data);
}

// Convert camelCase service name to snake_case for backend
function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * Update service preference for a specific service
 */
export async function updateServicePreference(
  service: string,
  mode: PreferenceMode,
  fallbackEnabled: boolean
): Promise<void> {
  // Convert imageGeneration -> image_generation for backend
  const backendService = toSnakeCase(service);
  // Backend expects query params on PUT /api/v1/local-first/config
  await api.put('/api/v1/local-first/config', null, {
    params: {
      service: backendService,
      mode,
      fallback_enabled: fallbackEnabled,
    },
  });
}

/**
 * Update auto-start setting
 */
export async function updateAutoStart(enabled: boolean): Promise<void> {
  await api.put('/api/v1/local-first/config/auto-start', {
    enabled,
  });
}

// ============================================================================
// API Functions - Health Monitoring
// ============================================================================

// Backend returns snake_case, frontend expects camelCase
interface BackendComponentHealth {
  component_id: string;
  status: HealthStatus;
  is_installed: boolean;
  is_running: boolean;
  last_check: string | null;
  error: string | null;
  uptime_seconds: number;
  restart_count: number;
  response_time_ms: number | null;
}

function transformHealthData(data: BackendComponentHealth): ComponentHealth {
  return {
    componentId: data.component_id,
    status: data.status,
    isInstalled: data.is_installed,
    isRunning: data.is_running,
    lastCheck: data.last_check,
    lastError: data.error,
    uptimeSeconds: data.uptime_seconds,
    restartCount: data.restart_count,
    responseTimeMs: data.response_time_ms,
  };
}

/**
 * Get health status for all components
 */
export async function getAllComponentHealth(): Promise<Record<string, ComponentHealth>> {
  // Backend ALWAYS wraps in { success, data } (api_server/local_routes/
  // local_first.py). No dual-envelope fallback (RULE #-2): one format.
  const response = await api.get<BackendEnvelope<Record<string, BackendComponentHealth>>>(
    '/api/v1/local-first/health'
  );
  // Transform snake_case to camelCase
  const result: Record<string, ComponentHealth> = {};
  for (const [key, value] of Object.entries(response.data.data)) {
    result[key] = transformHealthData(value);
  }
  return result;
}

/**
 * Start a specific component
 * Delegates to componentService for implementation
 */
export async function startComponent(componentId: string): Promise<boolean> {
  const response = await startComponentAPI(componentId);
  return response.status === 'started' || response.status === 'running';
}

/**
 * Stop a specific component
 * Delegates to componentService for implementation
 */
export async function stopComponent(componentId: string): Promise<boolean> {
  const response = await stopComponentAPI(componentId);
  return response.status === 'stopped';
}

// ============================================================================
// WebSocket - Real-time Health Updates
// ============================================================================

interface HealthMessage {
  type: 'initial_health' | 'health_update';
  components?: Record<string, ComponentHealth>;
  componentId?: string;
  health?: ComponentHealth;
}

/**
 * Subscribe to real-time health updates via WebSocket
 *
 * Uses exponential backoff with a maximum retry limit to prevent
 * infinite reconnection loops when the WebSocket endpoint is unavailable.
 *
 * @param onUpdate - Called when a single component's health changes
 * @param onInitial - Called with the initial health state of all components
 * @returns Cleanup function to close the WebSocket connection
 */
export function subscribeToHealthUpdates(
  onUpdate: (componentId: string, health: ComponentHealth) => void,
  onInitial: (components: Record<string, ComponentHealth>) => void
): () => void {
  const wsUrl = getWebSocketUrl('/ws/health');

  let ws: WebSocket | null = null;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let retryCount = 0;
  let isCleanedUp = false;

  // Configuration for reconnection
  const {
    maxRetries: MAX_RETRIES,
    baseDelayMs: BASE_DELAY_MS,
    maxDelayMs: MAX_DELAY_MS,
  } = RETRY_CONFIGS.WEBSOCKET;

  const getBackoffDelay = (attempt: number): number => {
    // Exponential backoff: 5s, 10s, 20s, capped at 30s
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
    return delay;
  };

  const connect = () => {
    if (isCleanedUp) return;

    // Stop trying after max retries
    if (retryCount >= MAX_RETRIES) {
      console.log(
        '[LocalFirstService] WebSocket max retries reached, stopping reconnection attempts. ' +
          'Health updates will rely on polling only.'
      );
      return;
    }

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[LocalFirstService] WebSocket connected for health updates');
        retryCount = 0; // Reset retry count on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data: HealthMessage = JSON.parse(event.data);

          if (data.type === 'initial_health' && data.components) {
            onInitial(data.components);
          } else if (data.type === 'health_update' && data.componentId && data.health) {
            onUpdate(data.componentId, data.health);
          }
        } catch (e) {
          console.error('[LocalFirstService] Failed to parse health message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('[LocalFirstService] WebSocket error:', error);
      };

      ws.onclose = () => {
        if (isCleanedUp) return;

        retryCount++;
        if (retryCount < MAX_RETRIES) {
          const delay = getBackoffDelay(retryCount);
          console.log(
            `[LocalFirstService] WebSocket closed, reconnecting in ${delay / 1000}s ` +
              `(attempt ${retryCount}/${MAX_RETRIES})`
          );
          reconnectTimeout = setTimeout(connect, delay);
        } else {
          console.log(
            '[LocalFirstService] WebSocket max retries reached, stopping reconnection attempts'
          );
        }
      };
    } catch (error) {
      if (isCleanedUp) return;

      console.error('[LocalFirstService] Failed to create WebSocket:', error);
      retryCount++;
      if (retryCount < MAX_RETRIES) {
        const delay = getBackoffDelay(retryCount);
        reconnectTimeout = setTimeout(connect, delay);
      }
    }
  };

  // Initial connection
  connect();

  // Return cleanup function
  return () => {
    isCleanedUp = true;
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get human-readable label for preference mode
 */
export function getPreferenceModeLabel(mode: PreferenceMode): string {
  const labels: Record<PreferenceMode, string> = {
    local_first: 'Local First',
    local_only: 'Local Only',
    cloud_first: 'Cloud First',
    cloud_only: 'Cloud Only',
  };
  return labels[mode];
}

/**
 * Get CSS color class for health status
 */
export function getHealthStatusColor(status: HealthStatus): string {
  const colors: Record<HealthStatus, string> = {
    healthy: 'text-green-600 dark:text-green-400',
    degraded: 'text-yellow-600 dark:text-yellow-400',
    unhealthy: 'text-red-600 dark:text-red-400',
    unknown: 'text-secondary-500 dark:text-secondary-400',
  };
  return colors[status];
}

/**
 * Get badge color classes for health status
 */
export function getHealthStatusBadgeColors(status: HealthStatus): string {
  const colors: Record<HealthStatus, string> = {
    healthy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    unhealthy: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    unknown: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300',
  };
  return colors[status];
}

/**
 * Format uptime in human-readable format
 */
export function formatUptime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}
